import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Badge } from "@components/ui/badge";
import { Skeleton } from "@components/ui/skeleton";
import { formatTitleCase } from "../lib/formatting";
import {
  getInboxThreads,
  ingestThread,
  type InboxThread,
  getReviewQueue,
  proposeWorkItem,
  approveEntity,
  type EntitySummary,
} from "../lib/auth";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  formatSlaBreachLabel,
  getSlaBadgeClassName,
  getSlaStateFromEntity,
} from "../lib/slaState";

type InboxPageProps = {
  navigate: (route: string) => void;
};

const statusOptions: Array<{ value: InboxThread["status"]; label: string }> = [
  { value: "new", label: "New" },
  { value: "triage", label: "Triage" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
];

const priorityOptions: Array<{
  value: InboxThread["priority"];
  label: string;
}> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const timeRangeOptions = [
  { value: "", label: "All time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
] as const;

const allOptionValue = "__all__";

function getInitialParam(key: string) {
  if (typeof window === "undefined") {
    return "";
  }
  const params = new URLSearchParams(window.location.search);
  return params.get(key) ?? "";
}

function buildFromDate(range: string): string | undefined {
  if (!range) {
    return undefined;
  }
  const now = new Date();
  switch (range) {
    case "24h":
      return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    case "7d":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    case "30d":
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    default:
      return undefined;
  }
}

const intakeFormSchema = z.object({
  subject: z.string(),
  author: z.string().trim().min(1, "Author is required."),
  customerName: z.string(),
  customerEmail: z.string(),
  body: z.string().trim().min(1, "Message body is required."),
});
type IntakeFormValues = z.infer<typeof intakeFormSchema>;
const intakeFormDefaults: IntakeFormValues = {
  subject: "",
  author: "",
  customerName: "",
  customerEmail: "",
  body: "",
};

function syncUrl(params: Record<string, string>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      search.set(key, value);
    }
  });
  const next = `${window.location.pathname}${search.toString() ? `?${search}` : ""}`;
  window.history.replaceState({}, "", next);
}

export function InboxPage({ navigate }: InboxPageProps) {
  const [threads, setThreads] = useState<InboxThread[]>([]);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [isInboxLoading, setIsInboxLoading] = useState(false);
  const [query, setQuery] = useState(() => getInitialParam("q"));
  const [statusFilter, setStatusFilter] = useState(() =>
    getInitialParam("status"),
  );
  const [priorityFilter, setPriorityFilter] = useState(() =>
    getInitialParam("priority"),
  );
  const [timeRange, setTimeRange] = useState(() => getInitialParam("range"));
  const [intakeError, setIntakeError] = useState<string | null>(null);
  const intakeForm = useForm<IntakeFormValues>({
    resolver: zodResolver(intakeFormSchema),
    defaultValues: intakeFormDefaults,
  });
  const [visibleCount, setVisibleCount] = useState(20);
  const [proposalByThreadId, setProposalByThreadId] = useState<
    Record<string, EntitySummary>
  >({});
  const [isProposalLoading, setIsProposalLoading] = useState(false);
  const hasActiveFilters = Boolean(
    query.trim() || statusFilter || priorityFilter || timeRange,
  );

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Inbox</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  const fromDate = useMemo(() => buildFromDate(timeRange), [timeRange]);

  useEffect(() => {
    let isActive = true;
    setIsInboxLoading(true);
    setInboxError(null);
    getInboxThreads({
      query: query.trim() || undefined,
      status: (statusFilter || undefined) as InboxThread["status"] | undefined,
      priority: (priorityFilter || undefined) as
        | InboxThread["priority"]
        | undefined,
      from: fromDate,
    })
      .then((data) => {
        if (isActive) {
          setThreads(data);
          setVisibleCount(20);
        }
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : "Unable to load inbox right now.";
        setInboxError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsInboxLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [fromDate, priorityFilter, query, statusFilter]);

  useEffect(() => {
    let isActive = true;
    setIsProposalLoading(true);
    getReviewQueue()
      .then((entities) => {
        if (!isActive) {
          return;
        }
        const mapped: Record<string, EntitySummary> = {};
        entities.forEach((entity) => {
          if (entity.threadId) {
            mapped[entity.threadId] = entity;
          }
        });
        setProposalByThreadId(mapped);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : "Unable to load proposal previews.";
        setInboxError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsProposalLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    syncUrl({
      q: query.trim(),
      status: statusFilter,
      priority: priorityFilter,
      range: timeRange,
    });
  }, [priorityFilter, query, statusFilter, timeRange]);

  const { pending: handleIngestPending, callback: handleIngest } =
    useAsyncCallback(
      intakeForm.handleSubmit(async (values) => {
        setIntakeError(null);
        try {
          await ingestThread({
            subject: values.subject.trim() || undefined,
            messages: [{ author: values.author, body: values.body }],
            customer: {
              name: values.customerName.trim() || undefined,
              email: values.customerEmail.trim() || undefined,
            },
          });
          intakeForm.reset(intakeFormDefaults);
          const updatedThreads = await getInboxThreads({
            query: query.trim() || undefined,
            status: (statusFilter || undefined) as
              | InboxThread["status"]
              | undefined,
            priority: (priorityFilter || undefined) as
              | InboxThread["priority"]
              | undefined,
            from: fromDate,
          });
          setThreads(updatedThreads);
        } catch (err) {
          const message =
            err instanceof Error
              ? err.message
              : "Unable to ingest conversation.";
          setIntakeError(message);
        }
      }),
    );

  const {
    pending: approveProposalPending,
    activeArguments: approvalArguments,
    callback: approveProposal,
  } = useAsyncCallback(async (proposalId: string, threadId: string) => {
    try {
      const result = await approveEntity(proposalId);
      setProposalByThreadId((current) => ({
        ...current,
        [threadId]: result,
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to approve work item.";
      setInboxError(message);
    }
  });
  const {
    pending: generateProposalPending,
    activeArguments: proposalArguments,
    callback: generateProposal,
  } = useAsyncCallback(async (threadId: string) => {
    try {
      const result = await proposeWorkItem(threadId);
      setProposalByThreadId((current) => ({
        ...current,
        [threadId]: result,
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to propose work item.";
      setInboxError(message);
    }
  });

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Support Lens"
        title="Inbox"
        description="Conversations waiting for review and structuring."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate("/triage-rules")}>
              Triage rules
            </Button>
            <Button variant="outline" onClick={() => navigate("/review")}>
              Review queue
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        <div className="space-y-4">
          <details className="rounded-xl border border-border bg-card/60">
            <summary className="flex min-h-11 cursor-pointer items-center rounded-lg px-4 py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Search &amp; filters
            </summary>
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-1">
                <CardTitle className="text-lg">Search & filters</CardTitle>
                <CardDescription>
                  Find conversations by keyword, status, priority, or time.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="inbox-search">Search</Label>
                  <Input
                    id="inbox-search"
                    type="text"
                    placeholder="Search sender, subject, or excerpt"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inbox-range">Time range</Label>
                  <Select
                    value={timeRange || allOptionValue}
                    onValueChange={(value) =>
                      setTimeRange(value === allOptionValue ? "" : value)
                    }
                  >
                    <SelectTrigger id="inbox-range">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeRangeOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value || allOptionValue}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inbox-status">Status</Label>
                  <Select
                    value={statusFilter || allOptionValue}
                    onValueChange={(value) =>
                      setStatusFilter(value === allOptionValue ? "" : value)
                    }
                  >
                    <SelectTrigger id="inbox-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={allOptionValue}>
                        All statuses
                      </SelectItem>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inbox-priority">Priority</Label>
                  <Select
                    value={priorityFilter || allOptionValue}
                    onValueChange={(value) =>
                      setPriorityFilter(value === allOptionValue ? "" : value)
                    }
                  >
                    <SelectTrigger id="inbox-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={allOptionValue}>
                        All priorities
                      </SelectItem>
                      {priorityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </details>
          {isInboxLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <Card
                  key={`inbox-skeleton-${index}`}
                  className="border-border bg-card/60"
                >
                  <CardHeader className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-6 w-2/3" />
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                  <CardFooter className="flex flex-wrap gap-2">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-9 w-36" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : inboxError ? (
            <p className="text-sm text-red-700 dark:text-red-400" role="alert">
              {inboxError}
            </p>
          ) : threads.length === 0 ? (
            <Empty className="border-border bg-card/60">
              <EmptyHeader>
                <EmptyTitle>
                  {hasActiveFilters ? "No matches yet" : "No conversations yet"}
                </EmptyTitle>
                <EmptyDescription className="text-muted-foreground">
                  {hasActiveFilters
                    ? "Try adjusting or clearing your filters."
                    : "Add a conversation below to begin reviewing customer work."}
                </EmptyDescription>
              </EmptyHeader>
              {hasActiveFilters ? (
                <EmptyContent>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setQuery("");
                      setStatusFilter("");
                      setPriorityFilter("");
                      setTimeRange("");
                    }}
                  >
                    Clear filters
                  </Button>
                </EmptyContent>
              ) : null}
            </Empty>
          ) : (
            <>
              {query.trim() || statusFilter || priorityFilter || timeRange ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-2 text-xs text-muted-foreground">
                  <span className="uppercase tracking-[0.2em] text-muted-foreground/70">
                    Active filters
                  </span>
                  {query.trim() ? (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      aria-label="Clear search filter"
                    >
                      <Badge variant="outline" className="text-foreground/90">
                        Search: {query.trim()} ✕
                      </Badge>
                    </button>
                  ) : null}
                  {statusFilter ? (
                    <button
                      type="button"
                      onClick={() => setStatusFilter("")}
                      aria-label="Clear status filter"
                    >
                      <Badge variant="outline" className="text-foreground/90">
                        Status: {formatTitleCase(statusFilter)} ✕
                      </Badge>
                    </button>
                  ) : null}
                  {priorityFilter ? (
                    <button
                      type="button"
                      onClick={() => setPriorityFilter("")}
                      aria-label="Clear priority filter"
                    >
                      <Badge variant="outline" className="text-foreground/90">
                        Priority: {formatTitleCase(priorityFilter)} ✕
                      </Badge>
                    </button>
                  ) : null}
                  {timeRange ? (
                    <button
                      type="button"
                      onClick={() => setTimeRange("")}
                      aria-label="Clear time range filter"
                    >
                      <Badge variant="outline" className="text-foreground/90">
                        Range:{" "}
                        {timeRangeOptions.find(
                          (option) => option.value === timeRange,
                        )?.label ?? timeRange}{" "}
                        ✕
                      </Badge>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      setStatusFilter("");
                      setPriorityFilter("");
                      setTimeRange("");
                    }}
                  >
                    <Badge variant="outline" className="text-foreground/90">
                      Reset all
                    </Badge>
                  </button>
                </div>
              ) : null}
              {threads.slice(0, visibleCount).map((thread) => {
                const proposal = proposalByThreadId[thread.id];
                const intentLabel = thread.intent
                  ? formatTitleCase(thread.intent)
                  : "Intent unavailable";
                const intentConfidenceLabel =
                  thread.intentConfidence !== undefined
                    ? `Confidence ${(thread.intentConfidence * 100).toFixed(0)}%`
                    : "Confidence unavailable";
                const proposalSlaState = proposal
                  ? getSlaStateFromEntity({
                      targetAt: proposal.slaTargetAt,
                      breachedAt: proposal.slaBreachedAt,
                    })
                  : undefined;
                const threadSlaState = thread.slaState ?? proposalSlaState;
                const threadSlaBadgeLabel =
                  threadSlaState?.status === "breached"
                    ? "SLA breached"
                    : threadSlaState?.status === "at_risk"
                      ? (formatSlaBreachLabel(
                          threadSlaState.minutesRemaining,
                        ) ?? "SLA at risk")
                      : null;
                return (
                  <Card key={thread.id} className="border-border bg-card/60">
                    <CardHeader className="space-y-1">
                      <CardTitle className="text-lg">
                        {thread.subject}
                      </CardTitle>
                      <CardDescription>
                        {thread.messageCount} messages - Updated{" "}
                        {new Date(thread.updatedAt).toLocaleString()}
                      </CardDescription>
                      {thread.customer ? (
                        <button
                          type="button"
                          className="text-left text-xs text-muted-foreground hover:text-foreground/90"
                          onClick={() =>
                            navigate(`/customers/${thread.customer?.id}`)
                          }
                        >
                          Customer:{" "}
                          {thread.customer.name ??
                            thread.customer.email ??
                            "Unknown customer"}
                        </button>
                      ) : (
                        <p className="text-xs text-muted-foreground/70">
                          Customer: Unassigned
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {thread.summary}
                      </p>
                      <p className="text-xs text-muted-foreground sm:hidden">
                        Intent: {intentLabel} · {intentConfidenceLabel}
                      </p>
                    </CardHeader>
                    <CardFooter className="flex flex-wrap gap-2">
                      {thread.triageRule ? (
                        <Badge variant="outline" className="text-foreground/90">
                          {thread.triageRule.name}
                        </Badge>
                      ) : null}
                      {thread.triageOwner ? (
                        <Badge variant="outline" className="text-foreground/90">
                          {formatTitleCase(thread.triageOwner.type)}{" "}
                          {thread.triageOwner.id}
                        </Badge>
                      ) : null}
                      <Badge
                        variant="outline"
                        className="text-foreground/90"
                        title={intentConfidenceLabel}
                        aria-label={`${intentLabel}. ${intentConfidenceLabel}.`}
                      >
                        {intentLabel}
                      </Badge>
                      <Badge variant="outline" className="text-foreground/90">
                        {formatTitleCase(thread.status)}
                      </Badge>
                      <Badge variant="outline" className="text-foreground/90">
                        {formatTitleCase(thread.priority)}
                      </Badge>
                      {threadSlaState && threadSlaBadgeLabel ? (
                        <Badge
                          variant="outline"
                          className={`${getSlaBadgeClassName(
                            threadSlaState.status,
                          )}`}
                        >
                          {threadSlaBadgeLabel}
                        </Badge>
                      ) : null}
                      <Button
                        variant="outline"
                        className="ml-auto md:ml-auto"
                        onClick={() => navigate(`/threads/${thread.id}`)}
                      >
                        View conversation
                      </Button>
                    </CardFooter>
                    <CardContent className="border-t border-border pt-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="space-y-1">
                          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
                            Proposed work
                          </p>
                          {proposal ? (
                            <>
                              <p className="text-sm text-foreground">
                                {proposal.title}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {proposal.type} · {proposal.priority}
                                {proposal.confidence !== undefined
                                  ? ` · Confidence ${(proposal.confidence * 100).toFixed(0)}%`
                                  : ""}
                              </p>
                              {proposal.slaTargetAt ? (
                                <p className="text-xs text-muted-foreground">
                                  SLA target:{" "}
                                  {new Date(
                                    proposal.slaTargetAt,
                                  ).toLocaleString()}
                                  {proposal.slaBreachedAt
                                    ? ` · Breached ${new Date(
                                        proposal.slaBreachedAt,
                                      ).toLocaleString()}`
                                    : ""}
                                </p>
                              ) : thread.slaState?.targetAt ? (
                                <p className="text-xs text-muted-foreground">
                                  SLA target:{" "}
                                  {new Date(
                                    thread.slaState.targetAt,
                                  ).toLocaleString()}
                                </p>
                              ) : null}
                            </>
                          ) : isProposalLoading ? (
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-40" />
                              <Skeleton className="h-3 w-28" />
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No proposal yet.
                            </p>
                          )}
                        </div>
                        {proposal ? (
                          proposal.status === "proposed" ? (
                            <Button
                              loading={
                                approveProposalPending &&
                                approvalArguments?.[0] === proposal.id
                              }
                              disabled={approveProposalPending}
                              loadingLabel="Approving…"
                              onClick={() =>
                                void approveProposal(proposal.id, thread.id)
                              }
                            >
                              Approve
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() =>
                                navigate(`/entities/${proposal.id}`)
                              }
                            >
                              Open work item
                            </Button>
                          )
                        ) : (
                          <Button
                            loading={
                              generateProposalPending &&
                              proposalArguments?.[0] === thread.id
                            }
                            disabled={generateProposalPending}
                            loadingLabel="Generating proposal…"
                            variant="outline"
                            onClick={() => void generateProposal(thread.id)}
                          >
                            Generate proposal
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {visibleCount < threads.length ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setVisibleCount((count) => count + 20)}
                >
                  Load more
                </Button>
              ) : null}
            </>
          )}
        </div>
        <details
          className="rounded-xl border border-border bg-card/60"
          open={Boolean(
            intakeError || Object.keys(intakeForm.formState.errors).length,
          )}
        >
          <summary className="flex min-h-11 cursor-pointer items-center rounded-lg px-4 py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Add a conversation manually
          </summary>
          <Card className="h-fit border-border bg-card/60">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg">Manual intake</CardTitle>
              <CardDescription>
                Add a customer conversation for triage and review.
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleIngest} noValidate>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="intake-subject">Subject</Label>
                  <Input
                    id="intake-subject"
                    type="text"
                    placeholder="Login issue"
                    {...intakeForm.register("subject")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intake-author">Author</Label>
                  <Input
                    id="intake-author"
                    type="text"
                    placeholder="Customer"
                    {...intakeForm.register("author")}
                    aria-invalid={Boolean(intakeForm.formState.errors.author)}
                    aria-describedby={
                      intakeForm.formState.errors.author
                        ? "intake-author-error"
                        : undefined
                    }
                  />
                  {intakeForm.formState.errors.author ? (
                    <p
                      id="intake-author-error"
                      className="text-xs text-red-700 dark:text-red-400"
                      role="alert"
                    >
                      {intakeForm.formState.errors.author.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intake-customer-name">Customer name</Label>
                  <Input
                    id="intake-customer-name"
                    type="text"
                    placeholder="Acme Corp"
                    {...intakeForm.register("customerName")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intake-customer-email">Customer email</Label>
                  <Input
                    id="intake-customer-email"
                    type="email"
                    placeholder="support@acme.com"
                    {...intakeForm.register("customerEmail")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intake-body">Message</Label>
                  <Textarea
                    id="intake-body"
                    placeholder="Can't sign in to my dashboard."
                    {...intakeForm.register("body")}
                    aria-invalid={Boolean(intakeForm.formState.errors.body)}
                    aria-describedby={
                      intakeForm.formState.errors.body
                        ? "intake-body-error"
                        : undefined
                    }
                  />
                  {intakeForm.formState.errors.body ? (
                    <p
                      id="intake-body-error"
                      className="text-xs text-red-700 dark:text-red-400"
                      role="alert"
                    >
                      {intakeForm.formState.errors.body.message}
                    </p>
                  ) : null}
                </div>
                {intakeError ? (
                  <p
                    className="text-sm text-red-700 dark:text-red-400"
                    role="alert"
                  >
                    {intakeError}
                  </p>
                ) : null}
                <Button
                  loading={handleIngestPending}
                  loadingLabel="Ingesting conversation…"
                  type="submit"
                  className="w-full"
                  disabled={intakeForm.formState.isSubmitting}
                >
                  {intakeForm.formState.isSubmitting
                    ? "Ingesting..."
                    : "Ingest conversation"}
                </Button>
              </CardContent>
            </form>
          </Card>
        </details>
      </section>
    </PageShell>
  );
}
